import { Constants } from "appConstants";
import { Partner } from "models/partner/Partner";
import {
	AddressFields,
	CityFields,
	EstablishmentFields,
	PartnerFields
} from "models/partner/types";
import { useEffect, useMemo, useState } from "react";

import useStandardToast from "hooks/useStandardToast";

import { BaseDetailsContent, HeadingDetails, Loading } from "components";
import { useNavigate, useParams } from "react-router-dom";
import { PartnerService } from "services";
import "../styles.scss";

export default function PartnerDetails() {
	const { id } = useParams();
	const navigate = useNavigate();
	const [partner, setPartner] = useState<Partner | undefined>(undefined);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const { showErrorToast } = useStandardToast();

	const partnerService = new PartnerService();

	useEffect(() => {
		setIsLoading(true);
		partnerService
			.getPartnerDetails(String(id))
			.then((partnerResponse: Partner) => {
				setPartner(partnerResponse);
			})
			.catch(() => {
				navigate("/backoffice/partners");
				showErrorToast(Constants.strings.errors.failedToGetPartnerDetails);
			})
			.finally(() => setIsLoading(false));
	}, [id]);

	const cleanPartner = useMemo(() => {
		if (!partner) return {};

		const establishment = partner?.[PartnerFields.ESTABLISHMENTS][0];
		const remark = establishment?.[EstablishmentFields.REMARK]
			? establishment[EstablishmentFields.REMARK]
			: "";
		const address = establishment?.[EstablishmentFields.ADDRESS];
		const adressComplement = address?.[AddressFields.COMPLEMENT]
			? `, ${address?.[AddressFields.COMPLEMENT]}`
			: "";
		const city = address?.[AddressFields.CITY];

		return {
			[PartnerFields.ID]: partner?.[PartnerFields.ID],
			[PartnerFields.DESCRIPTION]: partner?.[PartnerFields.DESCRIPTION],
			...(partner?.[PartnerFields.LOGO_URL] && {
				[PartnerFields.LOGO_URL]: (
					<img
						src={partner?.[PartnerFields.LOGO_URL]}
						alt={Constants.strings.fields?.[PartnerFields.LOGO_URL]}
					/>
				)
			}),
			...(partner?.[PartnerFields.ESTABLISHMENT_IMAGE_URL] && {
				[PartnerFields.ESTABLISHMENT_IMAGE_URL]: (
					<img
						src={partner?.[PartnerFields.ESTABLISHMENT_IMAGE_URL]}
						alt={Constants.strings.fields.establishment}
					/>
				)
			}),
			establishment: {
				[EstablishmentFields.TRADE_NAME]:
					establishment?.[EstablishmentFields.TRADE_NAME],
				[EstablishmentFields.CONTACT_PHONE]:
					establishment?.[EstablishmentFields.CONTACT_PHONE],
				[AddressFields.POSTAL_CODE]: address?.[AddressFields.POSTAL_CODE],
				[AddressFields.STREET]: `${address?.[AddressFields.STREET]} ${
					address?.[AddressFields.NUMBER]
				} ${adressComplement}`,
				[AddressFields.CITY]: `${city?.[CityFields.NAME]} / ${
					city?.[CityFields.STATE]
				} `,
				[EstablishmentFields.SITE_URL]:
					establishment?.[EstablishmentFields.SITE_URL]
			},
			taxData: {
				[EstablishmentFields.LEGAL_NAME]:
					establishment?.[EstablishmentFields.LEGAL_NAME],
				[EstablishmentFields.CNPJ]: establishment?.[EstablishmentFields.CNPJ],
				[EstablishmentFields.IE]: establishment?.[EstablishmentFields.IE]
			},
			responsible: {
				[EstablishmentFields.RESPONSIBLE_NAME]:
					establishment?.[EstablishmentFields.RESPONSIBLE_NAME],
				[EstablishmentFields.RESPONSIBLE_CPF]:
					establishment?.[EstablishmentFields.RESPONSIBLE_CPF],
				[EstablishmentFields.RESPONSIBLE_PHONE]:
					establishment?.[EstablishmentFields.RESPONSIBLE_PHONE],
				[EstablishmentFields.RESPONSIBLE_ROLE]:
					establishment?.[EstablishmentFields.RESPONSIBLE_ROLE],
				[EstablishmentFields.REMARK]: remark
			}
		};
	}, [partner]);

	return (
		<div className="partner-page">
			{!isLoading ? (
				partner && (
					<>
						<HeadingDetails title={partner.name} showEditButton={false} />
						<BaseDetailsContent
							data={cleanPartner}
							sidePanelFields={[
								PartnerFields.ID,
								PartnerFields.DESCRIPTION,
								...(partner?.[PartnerFields.LOGO_URL]
									? [PartnerFields.LOGO_URL]
									: []),
								...(partner?.[PartnerFields.ESTABLISHMENT_IMAGE_URL]
									? [PartnerFields.ESTABLISHMENT_IMAGE_URL]
									: [])
							]}
							mainPanelFieldNames={{
								establishment: Constants.strings.fields?.establishmentData,
								[EstablishmentFields.TRADE_NAME]:
									Constants.strings.fields?.[EstablishmentFields.TRADE_NAME],
								[EstablishmentFields.CONTACT_PHONE]:
									Constants.strings.fields?.[EstablishmentFields.CONTACT_PHONE],
								[AddressFields.POSTAL_CODE]:
									Constants.strings.fields?.[AddressFields.POSTAL_CODE],
								[AddressFields.STREET]:
									Constants.strings.fields?.[AddressFields.STREET],
								[AddressFields.CITY]:
									Constants.strings.fields?.[AddressFields.CITY],
								[EstablishmentFields.SITE_URL]:
									Constants.strings.fields?.[EstablishmentFields.SITE_URL],
								taxData: Constants.strings.fields.taxData,
								[EstablishmentFields.LEGAL_NAME]:
									Constants.strings.fields?.[EstablishmentFields.LEGAL_NAME],
								[EstablishmentFields.CNPJ]:
									Constants.strings.fields?.[EstablishmentFields.CNPJ],
								[EstablishmentFields.IE]:
									Constants.strings.fields?.[EstablishmentFields.IE],
								responsible: Constants.strings.fields.responsibleData,
								[EstablishmentFields.RESPONSIBLE_NAME]:
									Constants.strings.fields?.[
										EstablishmentFields.RESPONSIBLE_NAME
									],
								[EstablishmentFields.RESPONSIBLE_CPF]:
									Constants.strings.fields?.[
										EstablishmentFields.RESPONSIBLE_CPF
									],
								[EstablishmentFields.RESPONSIBLE_PHONE]:
									Constants.strings.fields?.[
										EstablishmentFields.RESPONSIBLE_PHONE
									],
								[EstablishmentFields.RESPONSIBLE_ROLE]:
									Constants.strings.fields?.[
										EstablishmentFields.RESPONSIBLE_ROLE
									],
								[EstablishmentFields.REMARK]:
									Constants.strings.fields?.[EstablishmentFields.REMARK]
							}}
							mainPanelFields={["establishment", "taxData", "responsible"]}
						/>
					</>
				)
			) : (
				<div className="partner-list-loading">
					<Loading type="primary" size={75} />
				</div>
			)}
		</div>
	);
}
